import * as React from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import { Redirects, Routes as AppRoutes, getNotFoundElement } from './Routes/Routes';
import { ErrorCatcher } from 'layouts/errorCatcher/ErrorCatcher';
import Plain from 'layouts/Plain';
import { BookingNavigationTracker } from 'Routes/BookingNavigationTracker/BookingNavigationTracker';
import { MntnTrackingPixel } from 'Routes/MntnTrackingPixel/MntnTrackingPixel';
import { InitialModal } from 'screens/InitialModals/InitialModal';
import { Maintenance } from 'screens/Maintenance/Maintenance';
import { isMaintenanceModeActive } from 'utilities/maintenanceMode';
import { RoutesPath } from 'utilities/pageUrl';

import { ScrollToTop } from './ScrollToTop';

const APP_NAME = 'Kindbody';

const ROUTE_TITLES: Record<string, string> = {
  [RoutesPath.DASHBOARD]: 'Dashboard',
  [RoutesPath.CREATE_ACCOUNT]: 'Create Account',
  [RoutesPath.APPOINTMENTS]: 'Appointments',
  [RoutesPath.BOOKING_QUIZ]: 'Booking Quiz',
  [RoutesPath.BOOKING_BOOK]: 'Book Appointment',
  [RoutesPath.ENTER_MEMBERSHIP_ACCESS_CODE]: 'Activate Membership',
  [RoutesPath.MEMBERSHIP_ACTIVATION_SUCCESS]: 'Membership Activation Success',
  [RoutesPath.VERIFY_MEMBERSHIP_ELIGIBILITY]: 'Verify Membership Eligibility',
  [RoutesPath.ENTER_MEMBERSHIP_UNIQUE_ID]: 'Enter Membership ID',
  [RoutesPath.ACTIVATE_MEMBERSHIP_WALMART_WIN_BID]: 'Activate Membership',
  [RoutesPath.ACTIVATE_MEMBERSHIP_WALMART_CONFIRM_IDENTITY]: 'Confirm Membership Identity',
  [RoutesPath.CONFIRM_MEMBERSHIP_ACTIVATION]: 'Confirm Membership Activation',
  [RoutesPath.RESULTS]: 'Results',
  [RoutesPath.RESULTS_V2]: 'Results',
  [RoutesPath.MY_JOURNEY]: 'My Journey',
  [RoutesPath.DOCUMENTS]: 'Documents',
  [RoutesPath.LOGIN]: 'Login',
  [RoutesPath.LOGIN_SSO_MEDTRONIC]: 'Login',
  [RoutesPath.LOGOUT]: 'Logout',
  [RoutesPath.FORGOT_PASSWORD]: 'Forgot Password',
  [RoutesPath.ACCESS_ACCOUNT]: 'Access Account',
  [`${RoutesPath.CONSENTS}/:id`]: 'Informed Consent',
  [RoutesPath.VERIFICATION_CODE]: 'Email Verification',
  [RoutesPath.MEDICATION_INSTRUCTIONS]: 'Medication Instructions',
  [RoutesPath.MEDICATION_INSTRUCTION]: 'Medication Instruction',
  [RoutesPath.MY_PROFILE]: 'My Profile',
  [RoutesPath.ACTIVATE_KINDBODY_BENEFIT]: 'Activate Kindbody Benefit',
  [RoutesPath.KINDBODY_BENEFIT]: 'Kindbody Benefit',
  [RoutesPath.INVOICES]: 'Invoices',
  [`${RoutesPath.INVOICES}/:id`]: 'Invoice Details',
  [RoutesPath.RESOURCES]: 'Resources',
  [RoutesPath.KINDBODY_360]: 'Kindbody 360',
  [RoutesPath.PATIENT_INTAKE]: 'Patient Intake',
  [RoutesPath.VIOS_PAYMENT]: 'VIOS Payment',
  [RoutesPath.VIOS_PAYMENTS]: 'VIOS Payments',
  [RoutesPath.VIRTUAL_MEETING]: 'Virtual Meeting',
  [RoutesPath.CHANGE_EMAIL]: 'Change Email',
  [RoutesPath.CONFIRM_NEW_EMAIL]: 'Confirm New Email',
  [RoutesPath.MESSAGES]: 'Messages',
  [`${RoutesPath.MESSAGES}/:categoryName`]: 'Messages',
  [`${RoutesPath.MESSAGES}/:categoryName/:conversationId`]: 'Messages',
  [RoutesPath.SOLERA_LANDING]: 'Solera',
  [RoutesPath.APPOINTMENT_CONFIRMATION]: 'Appointment Confirmation',
  [RoutesPath.MAINTENANCE]: 'Maintenance',
  '*': 'Page Not Found'
};

const formatPathAsTitle = (path: string) => {
  const normalizedPath = path
    .replace(/\/:\w+/g, '')
    .replace(/^\//, '')
    .replace(/\//g, ' ')
    .replace(/-/g, ' ')
    .trim();

  if (!normalizedPath) return APP_NAME;

  return normalizedPath.replace(/\b\w/g, letter => letter.toUpperCase());
};

const getDocumentTitle = (path: string) => {
  const routeTitle = ROUTE_TITLES[path] ?? formatPathAsTitle(path);

  if (routeTitle === APP_NAME) return APP_NAME;

  return `${APP_NAME} | ${routeTitle}`;
};

const RouteDocumentTitle = ({ path }: { path: string }) => (
  <Helmet>
    <title>{getDocumentTitle(path)}</title>
  </Helmet>
);

export const Router = () => {
  if (isMaintenanceModeActive()) {
    return (
      <BrowserRouter>
        <Plain>
          <RouteDocumentTitle path={RoutesPath.MAINTENANCE} />
          <ErrorCatcher id="maintenance">
            <Maintenance />
          </ErrorCatcher>
        </Plain>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <InitialModal />
      <BookingNavigationTracker />
      <MntnTrackingPixel />
      <ScrollToTop />
      <Routes>
        {Redirects}
        {AppRoutes.map(route => {
          const { path, Component, Layout } = route;
          const RouteLayout = Layout as React.ComponentType<React.PropsWithChildren>;

          return (
            <Route
              key={path}
              path={path}
              element={
                <RouteLayout>
                  <RouteDocumentTitle path={path} />
                  <ErrorCatcher id="route">
                    <Component />
                  </ErrorCatcher>
                </RouteLayout>
              }
            />
          );
        })}
        <Route
          path="*"
          element={
            <>
              <RouteDocumentTitle path="*" />
              {getNotFoundElement()}
            </>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};
